import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react'
import { translate, type Lang, type StringKey } from './strings'
import { supabase } from '../lib/supabase'

const STORAGE_KEY = 'escale.lang'

interface I18nValue {
  lang: Lang
  /** Traduit une cle, avec remplacement optionnel de {variables}. */
  t: (key: StringKey, vars?: Record<string, string | number>) => string
  setLang: (lang: Lang) => void
  toggleLang: () => void
}

const I18nContext = createContext<I18nValue | null>(null)

/**
 * Le francais est la langue par defaut. Le choix est retenu par
 * utilisateur : immediatement dans le navigateur pour que l'affichage
 * ne clignote pas au demarrage, et dans profiles.lang pour qu'il suive
 * la personne d'un telephone a l'autre.
 */
function readStoredLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'fr' || stored === 'en') return stored
  } catch {
    // Navigation privee ou stockage refuse : on garde le francais.
  }
  return 'fr'
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readStoredLang)

  const setLang = useCallback((next: Lang) => {
    setLangState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Sans stockage, le choix ne vaut que pour cette session.
    }
    // Enregistrement dans la base, sans bloquer l'affichage. Si la
    // connexion est mauvaise, la langue change quand meme a l'ecran.
    void supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        void supabase.from('profiles').update({ lang: next }).eq('id', data.user.id)
      }
    })
  }, [])

  const toggleLang = useCallback(() => {
    setLang(lang === 'fr' ? 'en' : 'fr')
  }, [lang, setLang])

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const value = useMemo<I18nValue>(
    () => ({
      lang,
      t: (key, vars) => translate(key, lang, vars),
      setLang,
      toggleLang,
    }),
    [lang, setLang, toggleLang],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n doit etre utilise dans un I18nProvider')
  return value
}
